<script setup lang="ts">
import { URL_OFFER_CREATE } from '@elements/tools/routes/routes';
import { trans } from '@elements/translation';
import type { MenuItem } from '@global-types/nav/menuItem';
import { computed, onMounted, ref } from 'vue';
import { ACCOUNT_PROFILE, DISPLAYED_MENU, PROFILE_MENU } from '../../config';
import { useNavbarMenu } from '../useNavbarMenu';
import UserMenuItem from './UserMenu/UserMenuItem.vue';

const emit = defineEmits([ 'close' ]);

const {
  toggleMenuPreference,
  wordingSwitchButton,
} = useNavbarMenu();

const props = defineProps({
  isMobile: {
    type: Boolean,
    default: () => false,
  },
  isCollapsed: {
    type: Boolean,
    default: () => false,
  },
  userProfile: {
    type: String,
    default: ACCOUNT_PROFILE.NO_PROFILE,
  },
});

onMounted(() => {
  document.addEventListener('click', (event) => {
    const target = event.target as HTMLElement;
    const menu = document.querySelector('.user-menu');
    const buttonDesktop = document.querySelector('.side-bar-button-user');
    const imageProfileDesktop = document.querySelector('#profile-picture-desktop');
    const buttonMobile = document.querySelector('.side-bar-button-user__mobile');
    // @TODO Centralize in pinia store, see https://bureaux-a-partager.atlassian.net/browse/MATCH-9839
    if (
      window.BREAKPOINTS.isMobile
      || window.BREAKPOINTS.isTablet
      || window.BREAKPOINTS.isTabletLarge
    ) {
      if (!buttonMobile || !menu) {
        return;
      }
      if (!menu.contains(target) && !buttonMobile.contains(target)) {
        emit('close');
      }
    }

    if (window.BREAKPOINTS.isDesktop) {
      if ((!buttonDesktop && !imageProfileDesktop) || !menu) {
        return;
      }
      if (
        !menu.contains(target)
        && !imageProfileDesktop?.contains(target)
        && !buttonDesktop?.contains(target)
      ) {
        emit('close');
      }
    }
  });
});

const menuType = ref(DISPLAYED_MENU.buyer);
const menuItems = computed<MenuItem[]>(() => {
  const menus: MenuItem[] = [ ...PROFILE_MENU ];

  if (
    (menuType.value === DISPLAYED_MENU.buyer && props.userProfile === ACCOUNT_PROFILE.BUYER)
    || props.userProfile === ACCOUNT_PROFILE.NO_PROFILE
  ) {
    menus.push(...[
      {
        name: 'divider',
        type: 'divider',
      } as MenuItem,
    ]);
    menus.push(...[
      {
        name: trans('user_menu.main.create_offer'),
        link: URL_OFFER_CREATE,
        icon: 'ph_plus',
        type: 'link',
      } as MenuItem,
    ]);
  }
  if (props.userProfile === ACCOUNT_PROFILE.BOTH) {
    menus.push(...[
      {
        name: 'divider',
        type: 'divider',
      } as MenuItem,
    ]);
    menus.push({
      name: wordingSwitchButton.value,
      click: toggleMenuPreference,
      icon: 'ph_shuffle',
      type: 'button',
    });
  }
  menus.push(...[
    {
      name: 'divider',
      type: 'divider',
    } as MenuItem,
  ]);
  menus.push({
    name: trans('global.disconnect'),
    type: 'link',
    link: '/logout',
    icon: 'ph_signout',
    iconColor: 'red',
    class: 'user-menu-item__disconnect',
  });
  return menus;
});
</script>

<template>
  <div class="user-menu" :class="{ 'collapsed': isCollapsed && !isMobile, 'user-menu__mobile': isMobile }">
    <ul class="user-menu__items">
      <UserMenuItem
        v-for="menuItem in menuItems"
        :key="menuItem.name"
        :menu-item="menuItem"
      />
    </ul>
  </div>
</template>
